import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  CardActionArea,
  CardContent,
  Stack,
} from "@mui/material";
import ButtonComponent from "./ButtonComponent";
import CustomMarkdownEditor from "./CustomMarkdownEditor";

import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DeleteIcon from "@mui/icons-material/Delete";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";

import "react-quill/dist/quill.snow.css"; // Quill styles
import "quill-emoji/dist/quill-emoji.css"; // Emoji styles
import PlaceholderInput from "./PlaceholderInput";

const CatalogTemplateEditor = ({
  templateType,
  headerType,
  setHeaderType,
  headerText,
  setHeaderText,
  headerFile,
  setHeaderFile,
  body,
  setBody,
  sampleBodyFields,
  setsampleBodyFields,
  sampleHeaderFields,
  setsampleHeaderFields,
  footer,
  setFooter,
  allButtons,
  setAllButtons,
  isButtonsValid,
  setIsButtonsValid,
  headerDuplicatePlaceholderError,
  setHeaderDuplicatePlaceholderError,
  bodyDuplicatePlaceholderError,
  setBodyDuplicatePlaceholderError,
  headerTextError,
  setHeaderTextError,
  location,
  setLocation,
  locationLatitudeError,
  locationLongitudeError,
  setLocationLatitudeError,
  setLocationLongitudeError,
}) => {
  const [fileError, setFileError] = useState(false);

  const detectPlaceholders = (text) => {
    const matches = text.match(/\{\{\d+\}\}/g) || [];
    const seen = new Set();
    let hasDuplicate = false;

    matches.forEach((match) => {
      if (seen.has(match)) {
        hasDuplicate = true;
      } else {
        seen.add(match);
      }
    });

    // Only keep the first occurrence of each placeholder
    const uniquePlaceholders = matches.filter(
      (match, idx) => matches.indexOf(match) === idx
    );
    return { hasDuplicate, uniquePlaceholders };
  };

  const validateHeaderText = (text) => {
    // Emoji ranges (emoticons, symbols, pictographs, etc.)
    const emojiRegex =
      /\p{Emoji_Presentation}|\p{Extended_Pictographic}️|[\u{1F1E6}-\u{1F1FF}]/u;

    // Disallowed formatting characters
    const formattingCharsRegex = /[*~]/;

    // Newline check
    const newlineRegex = /[\r\n]/;

    if (emojiRegex.test(text)) {
      return "Emojis are not permitted in the header.";
    }
    if (newlineRegex.test(text)) {
      return "Header cannot contain line breaks.";
    }
    if (formattingCharsRegex.test(text)) {
      return "Header cannot contain formatting characters (*, ~).";
    }
    return "";
  };

  const handleFooterChange = (event) => {
    const newFooter = event.target.value;

    if (newFooter === "" || newFooter.trim().length > 0) {
      if (newFooter.length <= 60) {
        setFooter(newFooter);
      }
    }
  };

  const handleHeaderFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // File type validation
    let allowedTypes = [];
    if (headerType === "image") {
      allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    } else if (headerType === "video") {
      allowedTypes = ["video/mp4"];
    } else if (headerType === "document") {
      allowedTypes = ["application/pdf"];
    }

    if (allowedTypes.length && !allowedTypes.includes(file.type)) {
      let typeMsg = "";
      if (headerType === "image") typeMsg = "image (.jpg, .png)";
      else if (headerType === "video") typeMsg = "video (.mp4)";
      else if (headerType === "document") typeMsg = "PDF (.pdf)";
      setFileError(`Invalid file type. Please upload a ${typeMsg} file.`);
      event.target.value = "";
      return;
    }

    // File size validation (existing logic)
    const maxSizes = {
      "video/mp4": 16 * 1024 * 1024, // 16MB for videos
      "image/jpeg": 5 * 1024 * 1024, // 5MB for images
      "image/jpg": 5 * 1024 * 1024, // 5MB for images
      "image/png": 5 * 1024 * 1024, // 5MB for images
      "application/pdf": 5 * 1024 * 1024, // 5MB for pdf
    };

    const maxSize = maxSizes[file.type];
    if (maxSize && file.size > maxSize) {
      const sizeLimit = file.type === "video/mp4" ? "16MB" : "5MB";
      const fileType =
        file.type === "video/mp4"
          ? "video"
          : file.type === "application/pdf"
          ? "PDF"
          : "image";
      setFileError(
        `File size exceeds the limit of ${sizeLimit} for ${fileType} files.`
      );
      event.target.value = "";
      return;
    }

    // Clear any previous file errors and set the file
    setFileError(false);
    setHeaderFile(file);
  };

  const handleDeleteHeaderFile = () => {
    setFileError(false); // Clear file error when file is deleted
    setHeaderFile(null);
  };

  const getHeaderFileInput = () => {
    const fileTypes = {
      image: {
        accept: ".jpg, .png",
        label: "Choose .JPG or .PNG *",
      },
      video: {
        accept: ".mp4",
        label: "Choose .MP4 file *",
      },
      document: {
        accept: ".pdf",
        label: "Choose .PDF file *",
      },
    };
    if (fileTypes[headerType]) {
      const { accept, label } = fileTypes[headerType];
      return (
        <div>
          <label htmlFor="file-upload">
            <Button
              variant="contained"
              component="span"
              sx={{ marginTop: 1, marginBottom: 1 }}
            >
              {label}
            </Button>
          </label>
          <input
            id="file-upload"
            type="file"
            accept={accept}
            onChange={handleHeaderFileChange}
            style={{ display: "none" }}
          />

          {/* Display file validation error */}
          {fileError && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mt: 1, display: "block" }}
            >
              {fileError}
            </Typography>
          )}

          {headerFile && (
            <div>
              <Typography variant="body2">
                File uploaded: {headerFile.name}
              </Typography>
              <IconButton aria-label="delete" onClick={handleDeleteHeaderFile}>
                <DeleteIcon />
              </IconButton>
            </div>
          )}
        </div>
      );
    } else {
      return null;
    }
  };

  useEffect(() => {
    if (location) {
      // Latitude validation
      if (
        location.latitude !== undefined &&
        location.latitude !== null &&
        location.latitude !== "" &&
        (location.latitude > 90 || location.latitude < -90)
      ) {
        setLocationLatitudeError(true);
      } else {
        setLocationLatitudeError(false);
      }

      // Longitude validation
      if (
        location.longitude !== undefined &&
        location.longitude !== null &&
        location.longitude !== "" &&
        (location.longitude > 180 || location.longitude < -180)
      ) {
        setLocationLongitudeError(true);
      } else {
        setLocationLongitudeError(false);
      }
    } else {
      setLocationLatitudeError(false);
      setLocationLongitudeError(false);
    }
  }, [location]);

  return (
    <Stack spacing={4}>
      {/* Body */}
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Grid item xs={12}>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  marginTop: 2,
                  marginBottom: 2,
                }}
              >
                <Typography variant="h6" sx={{ marginBottom: 1 }}>
                  Message body *
                </Typography>

                {/* Replace the textarea with our custom editor */}
                <CustomMarkdownEditor
                  id="template-body"
                  value={body}
                  onChange={(newValue) => {
                    setBody(newValue);
                    const { hasDuplicate, uniquePlaceholders } =
                      detectPlaceholders(newValue);
                    setBodyDuplicatePlaceholderError(hasDuplicate);

                    // Only update sample fields if no error
                    if (!hasDuplicate) {
                      const newSamples = uniquePlaceholders.map((_, idx) =>
                        sampleBodyFields[idx] !== undefined
                          ? sampleBodyFields[idx]
                          : ""
                      );
                      setsampleBodyFields(newSamples);
                    }
                    // On a transient duplicate, keep existing sample fields as-is
                    // so previously entered values aren't lost once it resolves.
                  }}
                  maxLength={1024}
                />

                {bodyDuplicatePlaceholderError && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    Duplicate variable placeholders are not allowed.
                  </Typography>
                )}
              </div>
            </Grid>
            {!bodyDuplicatePlaceholderError && sampleBodyFields.length > 0 && (
              <Grid item xs={11}>
                <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
                  Body Variables
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    width: "100%",
                    gap: 4,
                  }}
                >
                  {sampleBodyFields.map((sample, index) => (
                    <Box
                      key={index}
                      sx={{
                        marginBottom: "16px",
                        display: "flex",
                        flex: { xs: 1, md: 0.5 },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ display: "inline-block", mr: 1 }}
                      >
                        {"{{" + (index + 1) + "}}:"}
                      </Typography>
                      <PlaceholderInput
                        sample={sample}
                        sampleBodyFields={sampleBodyFields}
                        index={index}
                        setsampleBodyFields={setsampleBodyFields}
                      />
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}

            <Divider />

            {/* Footer */}
            <Grid item xs={12}>
              <Typography variant="h6">
                Footer <span style={{ opacity: 0.6 }}>(Optional)</span>
              </Typography>
              <TextField
                sx={{
                  width: "100%",
                  marginTop: 2,
                  marginBottom: 2,
                }}
                label="You can use this space to add a tagline, a way to unsubscribe, etc."
                value={footer}
                onChange={handleFooterChange}
                inputProps={{
                  maxLength: 60,
                  style: { paddingRight: "40px" },
                }}
                InputProps={{
                  endAdornment: (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "gray",
                        position: "absolute",
                        right: "10px",
                        top: "12px",
                      }}
                    >
                      {`${footer.length}/60`}
                    </Typography>
                  ),
                }}
              />
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      {/* Buttons */}
      <Card>
        <CardContent>
          <Box sx={{ width: "100%" }}>
            <Stack container spacing={2} sx={{ width: "100%" }}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Buttons
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Create buttons that let customers respond to your message or
                  take action.
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                  If you add more than three buttons, they will appear in a
                  list.
                </Typography>
              </Grid>

              {/* Render all buttons using the modular rendering function */}
              <>
                <Divider
                  sx={{
                    width: "100%",
                    mt: 2,
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flex: 1,
                      gap: 1,
                      flexWrap: {
                        xs: "wrap",
                        sm: "nowrap",
                      },
                    }}
                  >
                    <TextField label="Type" value="catalog" disabled />

                    <TextField
                      label="Button text"
                      value="View Catalog"
                      InputProps={{
                        endAdornment: (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "gray",
                              position: "absolute",
                              right: "10px",
                              top: "12px",
                            }}
                          >
                            12/25
                          </Typography>
                        ),
                      }}
                      fullWidth
                      required
                      disabled
                    />
                  </Box>
                </Box>
              </>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
};
export default CatalogTemplateEditor;
